import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Circle, Group, Layer, Line, Rect, Stage, Text } from 'react-konva'
import type Konva from 'konva'
import { nanoid } from 'nanoid'
import { useEditor, activeLevel } from '@/state/store'
import type { Opening, Point, PolygonPath, Prop, Space } from '@/model/types'
import { SPACE_KIND_BY_ID, SURFACE_BY_ID, defaultSpaceName } from '@/model/materials'
import { WallLayer } from '@/components/WallLayer'
import { OpeningLayer } from '@/components/OpeningLayer'
import { PropLayer } from '@/components/PropLayer'
import { Footprint2D } from '@/components/props/footprints'
import { PRESET_BY_ID } from '@/components/families/presets'
import { placementWidth } from '@/lib/openings'
import { niceGridStep, visibleWorldBounds, zoomAtPoint } from '@/lib/view'
import { snapPoint } from '@/lib/units'
import { area, bounds, centroid, closestPointOnPolygon, distance, rectPath, toFlatPoints } from '@/lib/geometry'
import { nearestOpeningTarget, openingFrame } from '@/lib/openings'

const SELECT_STROKE = '#fbbf24'
const PLOT_STROKE = '#6b7280'
const SETBACK_STROKE = '#b45309'

/** Stroke style that communicates how a space is enclosed. */
function wallStroke(wallMode: Space['wallMode'], scale: number) {
  switch (wallMode) {
    case 'full':
      return { stroke: '#d6deec', width: 2.4 / scale, dash: undefined as number[] | undefined }
    case 'half':
      return { stroke: '#9aa6bf', width: 2 / scale, dash: [0.6, 0.4] }
    case 'none':
      return { stroke: '#5b6b86', width: 1.2 / scale, dash: [0.18, 0.28] }
  }
}

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [size, setSize] = useState({ width: 800, height: 600 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setSize({ width: Math.max(1, width), height: Math.max(1, height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, size] as const
}

interface SpaceDrag {
  startWorld: Point
  originals: Map<string, PolygonPath>
  moved: boolean
  /** If non-shift click landed on an already-multi-selected space, collapse to it on click-release. */
  collapseTo: string | null
}

export function Canvas2D() {
  const [containerRef, size] = useElementSize<HTMLDivElement>()
  const view = useEditor((s) => s.view)
  const setView = useEditor((s) => s.setView)
  const tool = useEditor((s) => s.tool)
  const setTool = useEditor((s) => s.setTool)
  const select = useEditor((s) => s.select)
  const selectSpaces = useEditor((s) => s.selectSpaces)
  const toggleSpaceSelection = useEditor((s) => s.toggleSpaceSelection)
  const selection = useEditor((s) => s.selection)
  const update = useEditor((s) => s.update)
  const beginHistory = useEditor((s) => s.beginHistory)
  const setNameFocus = useEditor((s) => s.setNameFocus)
  const project = useEditor((s) => s.project)
  const level = useEditor(activeLevel)
  const drawKind = useEditor((s) => s.drawKind)
  const activePresetId = useEditor((s) => s.activePresetId)
  const activeOpeningPresetId = useEditor((s) => s.activeOpeningPresetId)
  const standards = project.settings.standards
  const snapInc = project.settings.gridSnap

  // The level immediately below the active one — shown as a faint ghost for alignment.
  const belowLevel = project.levels
    .filter((l) => l.baseElevation < level.baseElevation)
    .sort((a, b) => b.baseElevation - a.baseElevation)[0]

  const selectedIds = selection?.kind === 'spaces' ? selection.ids : []

  const stageRef = useRef<Konva.Stage>(null)
  const dragRef = useRef<SpaceDrag | null>(null)

  // --- drawing drafts -------------------------------------------------------
  const [rectDraft, setRectDraft] = useState<{ start: Point; current: Point } | null>(null)
  const [polyDraft, setPolyDraft] = useState<Point[] | null>(null)
  const [hoverPt, setHoverPt] = useState<Point | null>(null)
  // Snap preview for the opening tool: where the next door/window/gate would land.
  const [openingPreview, setOpeningPreview] = useState<{ center: Point; angle: number; width: number } | null>(null)
  // Snap preview for the prop tool: where the next prop would drop (footprint ghost).
  const [propPreview, setPropPreview] = useState<Point | null>(null)
  // While pressing on a space (for select / group-move), suppress stage panning.
  const [spaceInteracting, setSpaceInteracting] = useState(false)

  useEffect(() => {
    setRectDraft(null)
    setPolyDraft(null)
    setHoverPt(null)
    setOpeningPreview(null)
    setPropPreview(null)
  }, [tool])

  const isDrawing = tool === 'space-rect' || tool === 'space-poly' || tool === 'plot'
  const isPlacing = tool === 'opening' || tool === 'prop'
  const stageDraggable = !isDrawing && !isPlacing && !spaceInteracting

  function worldPointer(snapTo = true): Point | null {
    const stage = stageRef.current
    if (!stage) return null
    const p = stage.getRelativePointerPosition()
    if (!p) return null
    return snapTo ? snapPoint(p, snapInc) : p
  }

  // --- wheel zoom -----------------------------------------------------------
  function onWheel(e: Konva.KonvaEventObject<WheelEvent>) {
    e.evt.preventDefault()
    const stage = stageRef.current
    if (!stage) return
    const pointer = stage.getPointerPosition()
    if (!pointer) return
    const factor = e.evt.deltaY < 0 ? 1.1 : 1 / 1.1
    setView(zoomAtPoint(view, pointer, factor))
  }

  function onStageDragMove(e: Konva.KonvaEventObject<DragEvent>) {
    if (e.target !== stageRef.current) return
    const pos = e.target.position()
    setView({ ...view, x: pos.x, y: pos.y })
  }

  // --- space selection + group drag (handled at stage level) ----------------
  function onSpaceMouseDown(id: string, evt: MouseEvent) {
    if (tool !== 'select') return
    const additive = evt.shiftKey
    let resultIds: string[]
    let collapseTo: string | null = null
    if (additive) {
      resultIds = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
      toggleSpaceSelection(id)
    } else if (selectedIds.includes(id)) {
      resultIds = selectedIds // keep group for dragging
      if (selectedIds.length > 1) collapseTo = id
    } else {
      resultIds = [id]
      selectSpaces([id])
    }

    const start = worldPointer(false)
    if (!start) return
    const originals = new Map<string, PolygonPath>()
    for (const sp of level.spaces) {
      if (resultIds.includes(sp.id)) originals.set(sp.id, sp.polygon.map((p) => ({ ...p })))
    }
    dragRef.current = { startWorld: start, originals, moved: false, collapseTo }
    setSpaceInteracting(true)
    stageRef.current?.draggable(false) // belt-and-suspenders against stage pan
  }

  // --- drawing + drag interactions ------------------------------------------
  function onStageMouseDown(e: Konva.KonvaEventObject<MouseEvent>) {
    if (tool === 'space-rect') {
      const p = worldPointer()
      if (p) setRectDraft({ start: p, current: p })
    } else if (tool === 'space-poly') {
      const p = worldPointer()
      if (!p) return
      const pts = polyDraft ?? []
      if (pts.length >= 3 && distance(p, pts[0]) < snapInc * 1.5) {
        commitPolygon(pts)
        return
      }
      setPolyDraft([...pts, p])
    } else if (tool === 'opening') {
      if (e.target === e.target.getStage()) placeOpening()
    } else if (tool === 'prop') {
      // Props drop anywhere on the plan (incl. inside a space), so don't gate on the stage target.
      placeProp()
    } else if (tool === 'select') {
      // Click on empty canvas (stage itself) clears selection.
      if (e.target === e.target.getStage()) select(null)
    }
  }

  function findOpeningTarget(pt: Point) {
    const preset = PRESET_BY_ID[activeOpeningPresetId]
    if (!preset) return null
    const width = placementWidth(preset.familyId, preset.params, standards, project.plot)
    const target = nearestOpeningTarget(pt, preset.familyId, width, level.spaces, project.plot)
    if (!target) return null
    // Only accept clicks reasonably close to a wall centerline.
    if (target.dist > Math.max(1.5, 24 / view.scale)) return null
    return { target, preset, width }
  }

  function placeOpening() {
    const p = worldPointer(false)
    if (!p) return
    const found = findOpeningTarget(p)
    if (!found) return
    const { target, preset } = found
    const id = nanoid()
    const opening: Opening = {
      id,
      familyId: preset.familyId,
      presetId: preset.id,
      edgeIndex: target.edgeIndex,
      offset: target.offset,
      params: { ...preset.params } as Opening['params'],
    }
    update((draft) => {
      if (target.ownerId === 'compound') {
        draft.plot.compoundWall?.openings.push(opening)
      } else {
        const lvl = draft.levels.find((l) => l.id === level.id)
        const sp = lvl?.spaces.find((s) => s.id === target.ownerId)
        sp?.openings.push(opening)
      }
    })
    setTool('select')
    select({ kind: 'opening', ownerId: target.ownerId, id })
  }

  function placeProp() {
    const p = worldPointer()
    if (!p) return
    const preset = PRESET_BY_ID[activePresetId]
    if (!preset) return
    const id = nanoid()
    update((draft) => {
      const lvl = draft.levels.find((l) => l.id === level.id)
      if (!lvl) return
      lvl.props.push({
        id,
        familyId: preset.familyId,
        presetId: preset.id,
        params: { ...preset.params } as Prop['params'],
        position: p,
        rotation: 0,
        label: preset.label,
      })
    })
    setTool('select')
    select({ kind: 'prop', id })
  }

  function onStageMouseMove() {
    const drag = dragRef.current
    if (drag) {
      const cur = worldPointer(false)
      if (cur) {
        const off = snapPoint({ x: cur.x - drag.startWorld.x, y: cur.y - drag.startWorld.y }, snapInc)
        if (off.x !== 0 || off.y !== 0) {
          if (!drag.moved) {
            drag.moved = true
            beginHistory()
          }
          update(
            (draft) => {
              const lvl = draft.levels.find((l) => l.id === level.id)
              if (!lvl) return
              for (const sp of lvl.spaces) {
                const orig = drag.originals.get(sp.id)
                if (orig) sp.polygon = orig.map((pt) => ({ x: pt.x + off.x, y: pt.y + off.y }))
              }
            },
            { transient: true },
          )
        }
      }
      return
    }

    const p = worldPointer()
    if (!p) return
    if (tool === 'space-rect' && rectDraft) setRectDraft({ ...rectDraft, current: p })
    else if (tool === 'space-poly') setHoverPt(p)
    else if (tool === 'opening') {
      const raw = worldPointer(false)
      const found = raw ? findOpeningTarget(raw) : null
      if (found) {
        const frame = openingFrame(found.target.polygon, found.target.edgeIndex, found.target.offset)
        setOpeningPreview(frame ? { center: frame.center, angle: frame.angle, width: found.width } : null)
      } else {
        setOpeningPreview(null)
      }
    } else if (tool === 'prop') {
      setPropPreview(p)
    }
  }

  function onStageMouseUp() {
    const drag = dragRef.current
    if (drag) {
      if (!drag.moved && drag.collapseTo) selectSpaces([drag.collapseTo])
      dragRef.current = null
      setSpaceInteracting(false)
    }

    if (tool === 'space-rect' && rectDraft) {
      const { start, current } = rectDraft
      const x = Math.min(start.x, current.x)
      const y = Math.min(start.y, current.y)
      const w = Math.abs(current.x - start.x)
      const h = Math.abs(current.y - start.y)
      setRectDraft(null)
      if (w >= snapInc && h >= snapInc) addSpace(rectPath(x, y, w, h))
    }
  }

  function onDblClick() {
    if (tool === 'space-poly' && polyDraft && polyDraft.length >= 3) commitPolygon(polyDraft)
  }

  function commitPolygon(pts: Point[]) {
    setPolyDraft(null)
    setHoverPt(null)
    if (pts.length >= 3 && area(pts) >= snapInc * snapInc) addSpace(pts)
  }

  function addSpace(polygon: Point[]) {
    const id = nanoid()
    const preset = SPACE_KIND_BY_ID[drawKind]
    const count = level.spaces.length + 1
    update((draft) => {
      const lvl = draft.levels.find((l) => l.id === level.id)
      if (!lvl) return
      lvl.spaces.push({
        id,
        name: defaultSpaceName(drawKind, count),
        polygon,
        kind: drawKind,
        wallMode: preset.wallMode,
        hasCeiling: preset.hasCeiling,
        surface: preset.defaultSurface,
        floorOffset: 0,
        openings: [],
        openEdges: [],
      })
    })
    setTool('select')
    selectSpaces([id])
    setNameFocus(id) // jump focus to the name field so you can name it right away
  }

  // --- buildable footprint --------------------------------------------------
  const b = bounds(project.plot.boundary)
  const sb = project.plot.setbacks
  const buildable = rectPath(
    b.minX + sb.left,
    b.minY + sb.rear,
    b.maxX - b.minX - sb.left - sb.right,
    b.maxY - b.minY - sb.front - sb.rear,
  )

  // --- grid -----------------------------------------------------------------
  const step = niceGridStep(view)
  const vb = visibleWorldBounds(view, size.width, size.height)
  const gridLines: React.ReactNode[] = []
  const major = step * 5
  const startX = Math.floor(vb.minX / step) * step
  const startY = Math.floor(vb.minY / step) * step
  const pxWidth = 1 / view.scale
  for (let x = startX; x <= vb.maxX; x += step) {
    const isMajor = Math.abs(x % major) < 1e-6
    gridLines.push(
      <Line key={`vx${x.toFixed(3)}`} points={[x, vb.minY, x, vb.maxY]} stroke={isMajor ? '#2a3142' : '#1c2230'} strokeWidth={pxWidth} listening={false} />,
    )
  }
  for (let y = startY; y <= vb.maxY; y += step) {
    const isMajor = Math.abs(y % major) < 1e-6
    gridLines.push(
      <Line key={`hy${y.toFixed(3)}`} points={[vb.minX, y, vb.maxX, y]} stroke={isMajor ? '#2a3142' : '#1c2230'} strokeWidth={pxWidth} listening={false} />,
    )
  }

  // Clicking the canvas returns keyboard control to the editor: blur any focused form field
  // (e.g. the name input auto-focused after drawing) so V/R/P/D/Delete shortcuts work again.
  function blurFocusedField() {
    const a = document.activeElement as HTMLElement | null
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA')) a.blur()
  }

  return (
    <div ref={containerRef} className="canvas-host" onPointerDownCapture={blurFocusedField}>
      <Stage
        ref={stageRef}
        width={size.width}
        height={size.height}
        x={view.x}
        y={view.y}
        scaleX={view.scale}
        scaleY={view.scale}
        draggable={stageDraggable}
        onWheel={onWheel}
        onDragMove={onStageDragMove}
        onMouseDown={onStageMouseDown}
        onMouseMove={onStageMouseMove}
        onMouseUp={onStageMouseUp}
        onDblClick={onDblClick}
        style={{ cursor: isDrawing || isPlacing ? 'crosshair' : 'default' }}
      >
        <Layer listening={false}>{gridLines}</Layer>

        <Layer listening={false}>
          <Line points={toFlatPoints(project.plot.boundary)} closed stroke={PLOT_STROKE} strokeWidth={2 / view.scale} />
          <Line points={toFlatPoints(buildable)} closed stroke={SETBACK_STROKE} strokeWidth={1.5 / view.scale} dash={[0.5, 0.5]} />
        </Layer>

        {/* Ghost of the floor below, for alignment */}
        {belowLevel && (
          <Layer listening={false} opacity={0.5}>
            {belowLevel.spaces.map((sp) => (
              <Line
                key={sp.id}
                points={toFlatPoints(sp.polygon)}
                closed
                stroke="#48526b"
                strokeWidth={1.4 / view.scale}
                dash={[0.4, 0.4]}
              />
            ))}
          </Layer>
        )}

        <Layer>
          {level.spaces.map((space) => (
            <SpaceShape
              key={space.id}
              space={space}
              selected={selectedIds.includes(space.id)}
              singleSelected={selectedIds.length === 1 && selectedIds[0] === space.id}
              levelId={level.id}
              tool={tool}
              scale={view.scale}
              snapInc={snapInc}
              onMouseDownSpace={onSpaceMouseDown}
            />
          ))}

          {rectDraft && (
            <Rect
              x={Math.min(rectDraft.start.x, rectDraft.current.x)}
              y={Math.min(rectDraft.start.y, rectDraft.current.y)}
              width={Math.abs(rectDraft.current.x - rectDraft.start.x)}
              height={Math.abs(rectDraft.current.y - rectDraft.start.y)}
              fill={hexToRgba(SURFACE_BY_ID[SPACE_KIND_BY_ID[drawKind].defaultSurface].color, 0.3)}
              stroke={SELECT_STROKE}
              strokeWidth={1.5 / view.scale}
              listening={false}
            />
          )}

          {polyDraft && polyDraft.length > 0 && (
            <>
              <Line points={toFlatPoints(hoverPt ? [...polyDraft, hoverPt] : polyDraft)} stroke={SELECT_STROKE} strokeWidth={1.5 / view.scale} listening={false} />
              {polyDraft.map((p, i) => (
                <Circle key={i} x={p.x} y={p.y} radius={4 / view.scale} fill={i === 0 ? '#fbbf24' : '#9aa6bf'} listening={false} />
              ))}
            </>
          )}
        </Layer>

        <WallLayer />
        <OpeningLayer />
        <PropLayer />
        <SelectedSpaceHandles scale={view.scale} snapInc={snapInc} />

        {openingPreview && (
          <Layer listening={false}>
            <Group x={openingPreview.center.x} y={openingPreview.center.y} rotation={(openingPreview.angle * 180) / Math.PI}>
              <Line
                points={[-openingPreview.width / 2, 0, openingPreview.width / 2, 0]}
                stroke={SELECT_STROKE}
                strokeWidth={3 / view.scale}
                opacity={0.9}
              />
              <Circle x={0} y={0} radius={4 / view.scale} fill={SELECT_STROKE} />
            </Group>
          </Layer>
        )}

        {propPreview && (() => {
          const preset = PRESET_BY_ID[activePresetId]
          if (!preset) return null
          const ghostProp: Prop = {
            id: 'preview',
            familyId: preset.familyId,
            presetId: preset.id,
            params: { ...preset.params } as Prop['params'],
            position: propPreview,
            rotation: 0,
          }
          return (
            <Layer listening={false} opacity={0.45}>
              <Group x={propPreview.x} y={propPreview.y}>
                <Footprint2D prop={ghostProp} scale={view.scale} />
              </Group>
            </Layer>
          )
        })()}
      </Stage>

      <ScaleReadout step={step} snapInc={snapInc} selectedCount={selectedIds.length} />
    </div>
  )
}

// ---------------------------------------------------------------------------

function SpaceShape({
  space,
  selected,
  singleSelected,
  levelId,
  tool,
  scale,
  snapInc,
  onMouseDownSpace,
}: {
  space: Space
  selected: boolean
  singleSelected: boolean
  levelId: string
  tool: string
  scale: number
  snapInc: number
  onMouseDownSpace: (id: string, evt: MouseEvent) => void
}) {
  const update = useEditor((s) => s.update)

  const c = centroid(space.polygon)
  const interactive = tool === 'select'
  const surf = SURFACE_BY_ID[space.surface]
  const ws = wallStroke(space.wallMode, scale)
  const poly = space.polygon

  function insertVertex(worldPt: Point) {
    const { edgeIndex, point } = closestPointOnPolygon(worldPt, poly)
    const p = snapPoint(point, snapInc)
    update((draft) => {
      const sp = draft.levels.find((l) => l.id === levelId)?.spaces.find((s) => s.id === space.id)
      if (sp) sp.polygon.splice(edgeIndex + 1, 0, p)
    })
  }

  return (
    <Group>
      <Line
        points={toFlatPoints(poly)}
        closed
        fill={hexToRgba(surf.color, selected ? 0.5 : 0.34)}
        stroke={selected ? SELECT_STROKE : ws.stroke}
        strokeWidth={selected ? 2.6 / scale : ws.width}
        dash={ws.dash}
        strokeScaleEnabled={false}
        onMouseDown={(e) => {
          if (!interactive) return
          e.cancelBubble = true
          onMouseDownSpace(space.id, e.evt)
        }}
        onDblClick={(e) => {
          if (!singleSelected || !interactive) return
          e.cancelBubble = true
          const p = e.target.getStage()?.getRelativePointerPosition()
          if (p) insertVertex(p)
        }}
      />

      {/* Open-edge markers: the wall is simply gone there, so flag it (faint dashed line over the
          edge) — visible even when the space isn't selected, so it reads as intentional. */}
      {space.wallMode !== 'none' &&
        (space.openEdges ?? []).map((ei) => {
          const a = poly[ei % poly.length]
          const b = poly[(ei + 1) % poly.length]
          if (!a || !b) return null
          return (
            <Line
              key={`open${ei}`}
              points={[a.x, a.y, b.x, b.y]}
              stroke="#6b7a99"
              strokeWidth={1.4 / scale}
              dash={[0.5, 0.4]}
              listening={false}
            />
          )
        })}

      <Text
        x={c.x}
        y={c.y}
        text={`${space.name}\n${Math.round(area(poly))} sq ft`}
        fontSize={1.1}
        fill="#e6ebf5"
        align="center"
        offsetX={6}
        offsetY={1.2}
        width={12}
        listening={false}
      />
    </Group>
  )
}

/**
 * Reshape handles (gold corners + gray edge midpoints) for the single-selected space, rendered in a
 * dedicated TOP layer (mounted after walls/openings) so they're never hidden behind the wall band.
 */
function SelectedSpaceHandles({ scale, snapInc }: { scale: number; snapInc: number }) {
  const update = useEditor((s) => s.update)
  const beginHistory = useEditor((s) => s.beginHistory)
  const toggleSpaceEdgeWall = useEditor((s) => s.toggleSpaceEdgeWall)
  const tool = useEditor((s) => s.tool)
  const level = useEditor(activeLevel)
  const selection = useEditor((s) => s.selection)
  const edgeOrig = useRef<{ v0: Point; v1: Point; mid: Point } | null>(null)

  const id = selection?.kind === 'spaces' && selection.ids.length === 1 ? selection.ids[0] : null
  const space = id ? level.spaces.find((s) => s.id === id) : undefined
  if (tool !== 'select' || !space) return null
  const poly = space.polygon
  const openEdges = new Set(space.openEdges ?? [])
  const showWallToggles = space.wallMode !== 'none'
  const center = centroid(poly)

  function mutatePoly(fn: (p: PolygonPath) => void, transient = false) {
    update((draft) => {
      const sp = draft.levels.find((l) => l.id === level.id)?.spaces.find((s) => s.id === space!.id)
      if (sp) fn(sp.polygon)
    }, { transient })
  }
  function moveVertex(index: number, x: number, y: number) {
    mutatePoly((pg) => void (pg[index] = snapPoint({ x, y }, snapInc)), true)
  }
  function moveEdge(index: number, handleX: number, handleY: number) {
    const o = edgeOrig.current
    if (!o) return
    const d = snapPoint({ x: handleX - o.mid.x, y: handleY - o.mid.y }, snapInc)
    const j = (index + 1) % poly.length
    mutatePoly((pg) => {
      pg[index] = { x: o.v0.x + d.x, y: o.v0.y + d.y }
      pg[j] = { x: o.v1.x + d.x, y: o.v1.y + d.y }
    }, true)
  }
  function removeVertex(index: number) {
    if (poly.length <= 3) return
    mutatePoly((pg) => void pg.splice(index, 1))
  }
  function insertVertex(worldPt: Point) {
    const { edgeIndex, point } = closestPointOnPolygon(worldPt, poly)
    const p = snapPoint(point, snapInc)
    mutatePoly((pg) => void pg.splice(edgeIndex + 1, 0, p))
  }

  return (
    <Layer>
      {/* Edge midpoint handles — drag to move a whole edge; double-click adds a corner */}
      {poly.map((p, i) => {
        const q = poly[(i + 1) % poly.length]
        const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }
        return (
          <Rect
            key={`e${i}`}
            x={mid.x}
            y={mid.y}
            width={7 / scale}
            height={7 / scale}
            offsetX={3.5 / scale}
            offsetY={3.5 / scale}
            fill="#8fa0c4"
            stroke="#1c2230"
            strokeWidth={1 / scale}
            draggable
            onMouseDown={(e) => { e.cancelBubble = true }}
            onDragStart={() => {
              edgeOrig.current = { v0: { ...poly[i] }, v1: { ...poly[(i + 1) % poly.length] }, mid }
              beginHistory()
            }}
            onDragMove={(e) => moveEdge(i, e.target.x(), e.target.y())}
            onDragEnd={() => { edgeOrig.current = null }}
            onDblClick={(e) => { e.cancelBubble = true; insertVertex(mid) }}
          />
        )
      })}

      {/* Per-edge wall toggles — filled = walled, hollow/dashed = open. Offset toward the space
          interior so they don't collide with the gray edge-move squares or gold corner dots. */}
      {showWallToggles &&
        poly.map((p, i) => {
          const q = poly[(i + 1) % poly.length]
          const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }
          // Push inward toward the centroid by a pixel-constant offset.
          const dx = center.x - mid.x
          const dy = center.y - mid.y
          const d = Math.hypot(dx, dy) || 1
          const off = 15 / scale
          const cx = mid.x + (dx / d) * off
          const cy = mid.y + (dy / d) * off
          const open = openEdges.has(i)
          return (
            <Circle
              key={`w${i}`}
              x={cx}
              y={cy}
              radius={5 / scale}
              fill={open ? 'transparent' : '#2dd4bf'}
              stroke={open ? '#2dd4bf' : '#0f2e2a'}
              strokeWidth={1.4 / scale}
              dash={open ? [0.3, 0.25] : undefined}
              onMouseDown={(e) => { e.cancelBubble = true }}
              onClick={(e) => { e.cancelBubble = true; toggleSpaceEdgeWall(space!.id, i) }}
              onTap={(e) => { e.cancelBubble = true; toggleSpaceEdgeWall(space!.id, i) }}
            />
          )
        })}

      {/* Corner handles — drag to reshape; double-click removes the corner */}
      {poly.map((p, i) => (
        <Circle
          key={`v${i}`}
          x={p.x}
          y={p.y}
          radius={5 / scale}
          fill={SELECT_STROKE}
          stroke="#1c2230"
          strokeWidth={1 / scale}
          draggable
          onDragStart={() => beginHistory()}
          onDragMove={(e) => moveVertex(i, e.target.x(), e.target.y())}
          onMouseDown={(e) => { e.cancelBubble = true }}
          onDblClick={(e) => { e.cancelBubble = true; removeVertex(i) }}
        />
      ))}
    </Layer>
  )
}

function ScaleReadout({ step, snapInc, selectedCount }: { step: number; snapInc: number; selectedCount: number }) {
  return (
    <div className="scale-readout">
      grid {step < 1 ? `${step * 12}"` : `${step}'`} · snap {snapInc < 1 ? `${snapInc * 12}"` : `${snapInc}'`}
      {selectedCount > 1 && ` · ${selectedCount} selected`}
    </div>
  )
}
